import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, X, ArrowRight } from "lucide-react";

interface ParseIssue {
  severity: "critical" | "warning";
  message: string;
  action?: { label: string; path: string };
}

interface PostParseModalProps {
  dealId: string;
  issues: ParseIssue[];
  onDismiss: () => void;
}

/**
 * Modal that fires immediately after parsing completes when there are
 * issues that need attention. Replaces the pattern of warnings hidden
 * on tabs the user might never visit.
 */
export default function PostParseModal({
  dealId,
  issues,
  onDismiss,
}: PostParseModalProps) {
  const navigate = useNavigate();
  const criticalCount = issues.filter((i) => i.severity === "critical").length;
  const warningCount = issues.filter((i) => i.severity === "warning").length;

  if (issues.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 bg-bark-900/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between ${
          criticalCount > 0 ? "bg-red-50" : "bg-honey-50"
        }`}>
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${
              criticalCount > 0 ? "text-red-500" : "text-honey-600"
            }`} />
            <h3 className="font-display text-lg font-semibold text-bark-800">
              Parse Results
            </h3>
          </div>
          <button onClick={onDismiss} className="p-1 rounded text-bark-400 hover:text-bark-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Summary */}
        <div className="px-6 py-3 border-b border-grove-200">
          <div className="flex gap-3 text-sm">
            {criticalCount > 0 && (
              <span className="text-red-600 font-medium">{criticalCount} critical</span>
            )}
            {warningCount > 0 && (
              <span className="text-honey-600 font-medium">{warningCount} warning{warningCount !== 1 ? "s" : ""}</span>
            )}
          </div>
        </div>

        {/* Issues list */}
        <div className="px-6 py-4 max-h-64 overflow-y-auto space-y-2">
          {issues.map((issue, i) => (
            <div
              key={i}
              className={`px-3 py-2 rounded-lg text-sm flex items-start gap-2 ${
                issue.severity === "critical"
                  ? "bg-red-50 border border-red-200"
                  : "bg-honey-50 border border-honey-200"
              }`}
            >
              <span className="shrink-0 mt-0.5">
                {issue.severity === "critical" ? (
                  <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                ) : (
                  <AlertTriangle className="w-3.5 h-3.5 text-honey-500" />
                )}
              </span>
              <div className="flex-1">
                <p className={`${
                  issue.severity === "critical" ? "text-red-700" : "text-honey-700"
                }`}>
                  {issue.message}
                </p>
                {issue.action && (
                  <button
                    onClick={() => {
                      onDismiss();
                      navigate(`/deals/${dealId}/${issue.action!.path}`);
                    }}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-forest-600 hover:text-forest-700"
                  >
                    {issue.action.label} <ArrowRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-grove-50 border-t border-grove-200 flex justify-end gap-2">
          <button
            onClick={() => {
              onDismiss();
              navigate(`/deals/${dealId}/verify`);
            }}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-forest-600 text-white hover:bg-forest-700"
          >
            Open Verification
          </button>
          <button
            onClick={onDismiss}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-grove-200 text-bark-600 hover:bg-grove-300"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
